import { useEffect, useRef, type ReactNode } from "react";

import { isSigningOut, useAuth } from "../auth/useAuth";
import { LoadingState } from "./LoadingState";

/**
 * RequireAuth — feature facebook-login-branded-auth (Req 8.1, 8.3).
 *
 * Gate for authenticated-only routes. Replaces the old in-app splash / "Sign in
 * to continue" prompt: an unauthenticated visitor is redirected straight to the
 * Cognito Managed Login page (marketing owns all pre-auth messaging; the app is
 * an authenticated tool). Wrap every route that needs a session in this — EXCEPT
 * `/auth/callback` (the OAuth return path; wrapping it would loop forever).
 *
 * Three states:
 *   - auth.loading            → <LoadingState /> (session check in flight; Req 8.3)
 *   - !auth.user (settled)    → kick off the redirect, render <LoadingState />
 *   - authenticated           → render children
 *
 * The redirect runs in an effect (never during render) and goes through
 * `auth.signIn()` — the AuthContext helper that calls Amplify
 * `signInWithRedirect()` and records any failure as `auth.error` (surfaced on
 * the callback page). A ref makes it fire once per mount so React StrictMode's
 * double-invoked effect (and re-renders while the redirect is in flight) don't
 * trigger duplicate redirects.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { loading, user, signIn } = useAuth();
  const redirectStarted = useRef(false);

  useEffect(() => {
    // Don't re-login while a sign-out is navigating away (isSigningOut) — that
    // race is what made "Sign out" just refresh the page and stay logged in.
    if (!loading && !user && !redirectStarted.current && !isSigningOut()) {
      redirectStarted.current = true;
      void signIn();
    }
  }, [loading, user, signIn]);

  if (loading || !user) {
    return <LoadingState message="Signing you in…" />;
  }

  return <>{children}</>;
}
